<script setup lang="ts">
/**
 * Dropdown select on Reka primitives.
 *
 * Items are passed via the `options` prop (simple) or via the `items` slot
 * (custom rendering). Trigger is a 44px-tall touch target with 16px font.
 *
 * Keyboard nav (Arrow keys, Enter, Esc, type-ahead) and touch behaviour
 * come from Reka.
 */
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from 'reka-ui'
import { computed, useAttrs } from 'vue'

defineOptions({ inheritAttrs: false })
import { Check, ChevronDown } from '@lucide/vue'

import { cn } from '@/shared/lib/cn'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface Props {
  modelValue?: string | null
  options?: SelectOption[]
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  options: () => [],
  placeholder: 'Select…',
  disabled: false,
  invalid: false,
  size: 'md',
})

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const attrs = useAttrs()

// Reka's <SelectValue> caches the selected item's text at selection time,
// so a renamed option wouldn't update the trigger. When we own the
// options, render the live label ourselves; otherwise (custom `items`
// slot, or nothing selected) fall back to Reka's default + placeholder.
const selectedLabel = computed<string | null>(() => {
  if (props.modelValue == null) return null
  return props.options.find((o) => o.value === props.modelValue)?.label ?? null
})

const triggerClasses = computed(() =>
  cn(
    'inline-flex w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'aria-[invalid=true]:border-destructive',
    {
      sm: 'h-9 text-base md:text-sm',
      md: 'h-11 text-base md:text-sm',
      lg: 'h-12 text-base',
    }[props.size],
    (attrs.class as string) ?? '',
  ),
)
</script>

<template>
  <SelectRoot
    :model-value="modelValue ?? undefined"
    :disabled="disabled"
    @update:model-value="(v) => emit('update:modelValue', String(v))"
  >
    <SelectTrigger
      :class="triggerClasses"
      :aria-invalid="invalid || undefined"
      data-focus-visible-managed
      v-bind="attrs"
    >
      <SelectValue :placeholder="placeholder" class="truncate text-left">
        <template v-if="selectedLabel !== null">{{ selectedLabel }}</template>
      </SelectValue>
      <ChevronDown class="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </SelectTrigger>
    <SelectPortal>
      <SelectContent
        position="popper"
        :side-offset="4"
        class="z-59 overflow-hidden rounded-md border border-border bg-card text-card-foreground shadow-md min-w-(--reka-select-trigger-width) max-h-[min(60vh,18rem)]"
      >
        <SelectViewport class="p-1">
          <SelectGroup>
            <slot name="items">
              <SelectItem
                v-for="opt in options"
                :key="opt.value"
                :value="opt.value"
                :disabled="opt.disabled"
                class="relative flex h-11 cursor-pointer select-none items-center gap-2 rounded-sm pl-8 pr-3 text-base md:h-9 md:text-sm data-highlighted:bg-muted data-highlighted:outline-none data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed"
              >
                <SelectItemIndicator
                  class="absolute left-2 inline-flex size-4 items-center justify-center"
                >
                  <Check class="size-4" aria-hidden="true" />
                </SelectItemIndicator>
                <SelectItemText>{{ opt.label }}</SelectItemText>
              </SelectItem>
            </slot>
          </SelectGroup>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
